/**
 * @file index.jsx
 * @brief Renders the responsive site header, navigation links, language switcher, and unread badges.
 */
import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUnifiedAuth } from "../../hooks/useUnifiedAuth";
import { useUnreadCount } from "../../hooks/useUnreadCount";
import { useUserProfile } from "../../context/UserProfileContext";
import { useSiteSettings } from "../../context/SiteSettingsContext";
import styles from "./header.module.css";

/**
 * Displays the main application navigation with role-aware actions for desktop and mobile layouts.
 * @returns {JSX.Element} The header and its responsive navigation menus.
 */
export default function Header() {
    const auth = useUnifiedAuth();
    const { profile } = useUserProfile();
    const { siteSettings } = useSiteSettings();
    const { t, i18n } = useTranslation();
    const siteName = siteSettings?.site_name || "LeBonTrain";
    const [menuOpen, setMenuOpen] = useState(false);
    const location     = useLocation();
    const navRef       = useRef(null);
    const hamburgerRef = useRef(null);
    const { unread, moderationUnread, requestNotificationPermission } = useUnreadCount(auth, location.pathname);

    const groups           = profile?.groups || [];
    const isAdmin          = groups.includes("admins");
    const isAdminOrModerator = isAdmin || groups.includes("moderators");
    const isSeller         = groups.includes("sellers");

    useEffect(() => { setMenuOpen(false); }, [location.pathname]);

    useEffect(() => {
        if (!menuOpen) return;
        /**
         * Closes the mobile drawer when the pointer event originates outside the menu and trigger.
         * @param {PointerEvent} e The pointer event captured on the document.
         * @returns {void}
         */
        const handler = (e) => {
            if (
                navRef.current      && !navRef.current.contains(e.target) &&
                hamburgerRef.current && !hamburgerRef.current.contains(e.target)
            ) setMenuOpen(false);
        };
        document.addEventListener("pointerdown", handler);
        return () => document.removeEventListener("pointerdown", handler);
    }, [menuOpen]);

    useEffect(() => {
        document.body.style.overflow = menuOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [menuOpen]);

    const close = () => setMenuOpen(false);
    const currentLanguage = i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en";

    /**
        * Switches the active UI language when the requested locale differs from the current one.
        * @param {string} lng The locale code to activate.
        * @returns {void}
     */
    const switchLanguage = (lng) => {
        if (lng !== currentLanguage) i18n.changeLanguage(lng);
    };

    /**
        * Requests notification permission before navigating to the messages area.
        * @returns {void}
     */
    const handleMessagesClick = () => {
        requestNotificationPermission?.().catch(() => {});
        close();
    };

    /**
        * Requests notification permission before navigating to the moderation queue.
        * @returns {void}
     */
    const handleModerationClick = () => {
        requestNotificationPermission?.().catch(() => {});
        close();
    };

    const navClassName = ({ isActive }) =>
        `${styles.navLink} ${isActive ? styles.navLinkActive : ""}`;

    const navBadgeClassName = ({ isActive }) =>
        `${styles.navLinkBadge} ${isActive ? styles.navLinkBadgeActive : ""}`;

    const mobileNavClassName = ({ isActive }) =>
        `${styles.mobileNavLink} ${isActive ? styles.mobileNavLinkActive : ""}`;

    return (
        <header className={styles.header}>
            <div className={styles.container}>

                {/* Logo */}
                <Link to="/" className={styles.logo} onClick={close}>
                    <img
                        src={`${process.env.PUBLIC_URL}/favicon.svg`}
                        alt={`${siteName} icon`}
                        className={styles.logoIcon}
                    />
                    <span className={styles.logoText}>{siteName}</span>
                </Link>

                {/* Desktop nav */}
                <nav className={styles.desktopNav}>
                    <div className={styles.langSwitch}>
                        <button
                            type="button"
                            className={`${styles.langBtn} ${currentLanguage === "en" ? styles.langBtnActive : ""}`}
                            onClick={() => switchLanguage("en")}
                            aria-label={t("language.switchToEnglish")}
                        >
                            {t("language.english")}
                        </button>
                        <button
                            type="button"
                            className={`${styles.langBtn} ${currentLanguage === "fr" ? styles.langBtnActive : ""}`}
                            onClick={() => switchLanguage("fr")}
                            aria-label={t("language.switchToFrench")}
                        >
                            {t("language.french")}
                        </button>
                    </div>
                    {(isSeller || isAdminOrModerator) && (
                        <Link to="/listings/new" className={styles.sellLink}>
                            <span>+</span> {t("header.sell")}
                        </Link>
                    )}
                    {isAdminOrModerator && (
                        <NavLink to="/moderation" className={navBadgeClassName} onClick={handleModerationClick}>
                            {t("header.moderation")}
                            {moderationUnread > 0 && (
                                <span className={styles.badge}>{moderationUnread > 99 ? "99+" : moderationUnread}</span>
                            )}
                        </NavLink>
                    )}
                    {isAdmin && (
                        <NavLink to="/admin/catalog" className={navClassName}>{t("header.catalog")}</NavLink>
                    )}
                    {isAdmin && (
                        <NavLink to="/admin/settings" className={navClassName}>{t("header.settings")}</NavLink>
                    )}
                    {auth.isAuthenticated && (
                        <>
                            <NavLink to="/favorites" className={navClassName}>{t("header.favorites")}</NavLink>
                            <NavLink to="/messages" className={navBadgeClassName} onClick={handleMessagesClick}>
                                {t("header.messages")}
                                {unread > 0 && (
                                    <span className={styles.badge}>{unread > 99 ? "99+" : unread}</span>
                                )}
                            </NavLink>
                            <NavLink to="/account/home" className={navClassName}>{t("header.profile")}</NavLink>
                            <button onClick={() => auth.logout()} className={styles.authButton}>
                                {t("header.logout")}
                            </button>
                        </>
                    )}
                    {!auth.isAuthenticated && (
                        <button onClick={() => auth.signinRedirect()} className={styles.authButton}>
                            {t("app.signIn")}
                        </button>
                    )}
                </nav>

                {/* Hamburger */}
                <button
                    ref={hamburgerRef}
                    className={`${styles.hamburger} ${menuOpen ? styles.hamburgerOpen : ""}`}
                    onClick={() => setMenuOpen((v) => !v)}
                    aria-label={menuOpen ? t("header.closeMenu") : t("header.openMenu")}
                    aria-expanded={menuOpen}
                >
                    <span className={styles.bar} />
                    <span className={styles.bar} />
                    <span className={styles.bar} />
                </button>
            </div>

            {/* Mobile backdrop */}
            {menuOpen && <div className={styles.backdrop} onClick={close} />}

            {/* Mobile drawer */}
            <nav ref={navRef}
                className={`${styles.mobileNav} ${menuOpen ? styles.mobileNavOpen : ""}`}
                aria-hidden={!menuOpen}>
                <div className={styles.mobileNavInner}>
                    <div className={styles.mobileLangSwitch}>
                        <button
                            type="button"
                            className={`${styles.mobileLangBtn} ${currentLanguage === "en" ? styles.mobileLangBtnActive : ""}`}
                            onClick={() => switchLanguage("en")}
                        >
                            {t("language.english")}
                        </button>
                        <button
                            type="button"
                            className={`${styles.mobileLangBtn} ${currentLanguage === "fr" ? styles.mobileLangBtnActive : ""}`}
                            onClick={() => switchLanguage("fr")}
                        >
                            {t("language.french")}
                        </button>
                    </div>
                    {(isSeller || isAdminOrModerator) && (
                        <Link to="/listings/new" className={styles.mobileSellLink} onClick={close}>
                            <span className={styles.mobileLinkIcon}>+</span> {t("header.sell")}
                        </Link>
                    )}
                    {isAdminOrModerator && (
                        <NavLink to="/moderation" className={mobileNavClassName} onClick={handleModerationClick}>
                            <span className={styles.mobileLinkIcon}>🛡</span> {t("header.moderation")}
                            {moderationUnread > 0 && (
                                <span className={styles.mobileBadge}>{moderationUnread > 99 ? "99+" : moderationUnread}</span>
                            )}
                        </NavLink>
                    )}
                    {isAdmin && (
                        <NavLink to="/admin/catalog" className={mobileNavClassName} onClick={close}>
                            <span className={styles.mobileLinkIcon}>🗂</span> {t("header.catalog")}
                        </NavLink>
                    )}
                    {isAdmin && (
                        <NavLink to="/admin/settings" className={mobileNavClassName} onClick={close}>
                            <span className={styles.mobileLinkIcon}>⚙</span> {t("header.settings")}
                        </NavLink>
                    )}
                    {auth.isAuthenticated && (
                        <>
                            <NavLink to="/favorites" className={mobileNavClassName} onClick={close}>
                                <span className={styles.mobileLinkIcon}>❤️</span> {t("header.favorites")}
                            </NavLink>
                            <NavLink to="/messages" className={mobileNavClassName} onClick={handleMessagesClick}>
                                <span className={styles.mobileLinkIcon}>💬</span>
                                {t("header.messages")}
                                {unread > 0 && (
                                    <span className={styles.mobileBadge}>{unread > 99 ? "99+" : unread}</span>
                                )}
                            </NavLink>
                            <NavLink to="/account/home" className={mobileNavClassName} onClick={close}>
                                <span className={styles.mobileLinkIcon}>👤</span> {t("header.profile")}
                            </NavLink>

                            <div className={styles.mobileDivider} />

                            <button
                                onClick={() => { auth.logout(); close(); }}
                                className={styles.mobileAuthButton}
                            >
                                {t("header.logout")}
                            </button>
                        </>
                    )}
                    {!auth.isAuthenticated && (
                        <>
                            <div className={styles.mobileDivider} />
                            <button
                                onClick={() => { auth.signinRedirect(); close(); }}
                                className={styles.mobileAuthButton}
                            >
                                {t("app.signIn")}
                            </button>
                        </>
                    )}
                </div>
            </nav>
        </header>
    );
}